import Cookies from 'js-cookie';

const isJSONString = value => {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed !== null && typeof parsed === 'object';
  } catch {
    return false;
  }
};

const parseStorageValue = (value, parseJSON = true) => {
  if (parseJSON && isJSONString(value)) {
    return JSON.parse(value);
  }

  return value;
};

const transformStorageValue = (value, serializeJSON = true) => {
  const isJSONValue = value !== null && typeof value === 'object';

  if (isJSONValue && serializeJSON) {
    return JSON.stringify(value);
  }

  return value;
};

const getStorage = persist => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (persist) {
    return window.localStorage;
  }

  return window.sessionStorage;
};

const setStorageItem = ({ key, value, persist = false, serializeJSON = true }) => {
  try {
    const storage = getStorage(persist);

    if (!storage) {
      return false;
    }

    if (!key) {
      throw new Error('Storage key is required');
    }

    const storageValue = transformStorageValue(value, serializeJSON);
    storage.setItem(key, storageValue);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error setting ${persist ? 'localStorage' : 'sessionStorage'}:`, error);
    return false;
  }
};

const getStorageItem = ({ key, persist = false, parseJSON = true, defaultValue = null }) => {
  try {
    const storage = getStorage(persist);

    if (!storage) {
      return defaultValue;
    }

    if (!key) {
      throw new Error('Storage key is required');
    }

    const value = storage.getItem(key);

    if (value === null) {
      return defaultValue;
    }

    return parseStorageValue(value, parseJSON);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error getting ${persist ? 'localStorage' : 'sessionStorage'}:`, error);
    return defaultValue;
  }
};

const removeStorageItem = ({ key, persist = false }) => {
  try {
    const storage = getStorage(persist);

    if (!storage) {
      return false;
    }

    if (!key) {
      throw new Error('Storage key is required');
    }

    storage.removeItem(key);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error removing ${persist ? 'localStorage' : 'sessionStorage'}:`, error);
    return false;
  }
};

const setCookieItem = ({ key, value, options = {}, serializeJSON = true }) => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!key) {
      throw new Error('Storage key is required');
    }

    const cookieValue = transformStorageValue(value, serializeJSON);
    const { path = '/', sameSite = 'Lax', secure = true, expires = undefined, domain = undefined } = options;

    Cookies.set(key, cookieValue, { path, sameSite, secure, expires, domain });
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error setting cookie:', error);
    return false;
  }
};

const getCookieItem = ({ key, parseJSON = true, defaultValue = null }) => {
  try {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    if (!key) {
      throw new Error('Storage key is required');
    }

    const value = Cookies.get(key);

    if (value === undefined) {
      return defaultValue;
    }

    return parseStorageValue(value, parseJSON);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting cookie:', error);
    return defaultValue;
  }
};

const removeCookieItem = ({ key, options = {} }) => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!key) {
      throw new Error('Storage key is required');
    }

    const { path = '/', domain = undefined } = options;

    Cookies.remove(key, { path, domain });
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error removing cookie:', error);
    return false;
  }
};

const local = {
  set: params => setStorageItem({ ...params, persist: true }),
  get: params => getStorageItem({ ...params, persist: true }),
  remove: params => removeStorageItem({ ...params, persist: true }),
};

const session = {
  set: params => setStorageItem({ ...params, persist: false }),
  get: params => getStorageItem({ ...params, persist: false }),
  remove: params => removeStorageItem({ ...params, persist: false }),
};

const cookies = {
  set: setCookieItem,
  get: getCookieItem,
  remove: removeCookieItem,
};

const BrowserStorage = {
  local,
  session,
  cookies,
};

export default BrowserStorage;
